import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep, Dictionary, groupBy, orderBy } from "lodash";
import { PageHeader, PageHeaderRight, TooltipWrapper } from "@app/shared";
import { QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, SortDirections, TextAlignment, Waffles, printNumberAsString } from "@app/shared/waffles";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingDashboardDetails, BenchmarkingDashboardDetailsReportFlyout, BenchmarkingDashboardDetailsReportTypeEnum, BenchmarkingFiltersOverview, BenchmarkingProviderFlyout } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { getNetworkTooltip } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/utility";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { BenchmarkingLineOfBusinessEnum, BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { getClaimsVolumeDefinitions, getEfficiencyAndEffectivenessDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { NPF } from "@app/projects/servicing-provider/types";
import { NewActionMenu } from "@app/shared/action-menu";
import { FlyoutLink } from "@app/shared/Flyouts";

interface BenchmarkingDashboardDetailsViewProps {
  filtersPayload: NetworkComparisonFiltersPayload;
  onBackToFullViewClick: () => void;
  totalCount: number;
}

interface BenchmarkingDashboardDetailsViewWaffleToppings {
  commercialClaimsLabel: string;
  effectivenessRank: string;
  efficiencyRank: string;
  key: string;
  medicaidClaimsLabel: string;
  medicareClaimsLabel: string;
  networkCount: string;
  networkList: JSX.Element;
  providerName: JSX.Element;
  specialtyGroupName: string;
  totalClaimsLabel: string;
}

const DashboardDetailsView = (props: BenchmarkingDashboardDetailsViewProps): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardDetailsData, setDashboardDetailsData] = useState<BenchmarkingDashboardDetails[]>([]);
  const [dashboardDetailsViewToppings, setDashboardDetailsViewToppings] = useState<BenchmarkingDashboardDetailsViewWaffleToppings[]>([]);

  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  useEffect(() => {
    getDashboardDetails();
  }, []);

  const columnConfig: ColumnConfig = {
    commercialClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Commercial Claims Volume",
      noSort: props.totalCount > 500,
      testId: "commercial-claims-volume",
      tooltip: getClaimsVolumeDefinitions(), 
    },
    effectivenessRank: {
      alignment: TextAlignment.Left,
      displayText: "Effectiveness",
      noSort: props.totalCount > 500,
      testId: "effectiveness",
      tooltip: getEfficiencyAndEffectivenessDefinitions(NPF.EffectivenessRank),
    },
    efficiencyRank: {
      alignment: TextAlignment.Left,
      displayText: "Efficiency",
      noSort: props.totalCount > 500,
      testId: "efficiency",
      tooltip: getEfficiencyAndEffectivenessDefinitions(NPF.EfficiencyRank),
    },
    medicaidClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Medicaid Claims Volume",
      noSort: props.totalCount > 500,
      testId: "medicaid-claims-volume",
      tooltip: getClaimsVolumeDefinitions(), 
    },
    medicareClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Medicare Claims Volume",
      noSort: props.totalCount > 500,
      testId: "medicare-claims-volume",
      tooltip: getClaimsVolumeDefinitions(), 
    },
    networkCount: {
      alignment: TextAlignment.Left,
      displayText: "Network Count",
      noSort: props.totalCount > 500,
      testId: "network-count",
      tooltip: "Count of selected networks a provider is contracted with",
    },
    networkList: {
      alignment: TextAlignment.Left,
      displayText: "Network List",
      noSort: true,
      testId: "network-list",
      tooltip: "List of all selected networks a provider is contracted with",
    },
    providerName: {
      alignment: TextAlignment.Left,
      displayText: "Name",
      noSort: props.totalCount > 500,
      testId: "name",
    },
    specialtyGroupName: {
      alignment: TextAlignment.Left,
      displayText: "Specialty",
      hidden: props.filtersPayload.specialtyGroupId === 0,
      noSort: props.totalCount > 500,
      testId: "specialty-group-name",
    },
    totalClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Total Claims Volume",
      noSort: props.totalCount > 500,
      testId: "total-claims-volume",
      tooltip: getClaimsVolumeDefinitions(), 
    },
  };

  const customSort = (
    records: BenchmarkingDashboardDetailsViewWaffleToppings[],
    column: keyof BenchmarkingDashboardDetailsViewWaffleToppings,
    sortDirection: SortDirections
  ): BenchmarkingDashboardDetailsViewWaffleToppings[] => {
    let sortedData: BenchmarkingDashboardDetailsViewWaffleToppings[] = [];

    const getDashboardDetail = (npi: string): BenchmarkingDashboardDetails => {
      return dashboardDetailsData.find(x => x.npi === npi);
    }

    switch (column) {
      case "providerName":
        sortedData = orderBy(records, (d) => getDashboardDetail(d.key)?.providerName, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "efficiencyRank":
      case "effectivenessRank":
      case "networkCount":
        sortedData = orderBy(records, (d) => getDashboardDetail(d.key)?.[column], sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "commercialClaimsLabel":
      case "medicaidClaimsLabel":
      case "medicareClaimsLabel":
      case "specialtyGroupName":
      case "totalClaimsLabel":
        sortedData = orderBy(records, (d) => d[column], sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      default:
        break;
    }

    return sortedData;
  }

  const formatNetworkList = (networkList: string[]): JSX.Element => {
    const networks: BenchmarkingNetworkType[] = [];

    for (const networkId of networkList) {
      const network: BenchmarkingNetworkType = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === networkId);
      networks.push(network);
    }

    const groupedNetworks: Dictionary<BenchmarkingNetworkType[]> = groupBy(networks, "parentOrganization");

    const getNetworkDisplay = (n: BenchmarkingNetworkType) => {
      switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
        case BenchmarkingLineOfBusinessEnum.Marketplace:
          return (
            <TooltipWrapper
              text={getNetworkTooltip(n as QHPNetwork)}
            >
              {(n as QHPNetwork).networkName}
            </TooltipWrapper>
          );
        case BenchmarkingLineOfBusinessEnum.Medicare:
          return n.contractId;
        default:
          return;
      }
    }

    return (
      <React.Fragment>
        {Object.entries(groupedNetworks).map(([key, value], parentOrganizationIndex) => (
          <div key={parentOrganizationIndex}>
            <div>
              {key}
            </div>
            {value.map((network, networkIndex) => (
              <div className="ml-3" key={networkIndex}>
                {getNetworkDisplay(network)}
              </div>
            ))}
          </div>
        ))}
      </React.Fragment>
    );
  }

  const getDashboardDetails = (): void => {
    setLoading(true);

    const buildData = (data: BenchmarkingDashboardDetails[]): void => {
      if (!!data) {
        setDashboardDetailsData(data);

        const waffleToppings: BenchmarkingDashboardDetailsViewWaffleToppings[] = data.map((summary, index) => {
          /* eslint-disable sort-keys */
          return {
            key: summary.npi,
            providerName: (
              <React.Fragment>
                <div className="d-block">
                  <FlyoutLink
                    classNames="qes-button qes-button-secondary"
                    testId={`benchmarking-dashboard--dashboard-details__flyout-link-${index}`}
                    content={{
                      body: (
                        <BenchmarkingProviderFlyout
                          dashboardDetails={summary}
                          formatNetworkList={formatNetworkList}
                        />
                      ),
                      title: "",
                    }}
                  >
                    {!!summary.providerName ? summary.providerName : summary.npi}
                  </FlyoutLink>
                </div>
                {!!summary.providerName && (
                  <div className="d-block text-muted">
                    {summary.npi}
                  </div>
                )}
              </React.Fragment>
            ),
            specialtyGroupName: summary.specialtyGroupName,
            effectivenessRank: !!summary.effectivenessRank ? summary.effectivenessRank.toString() : "---",
            efficiencyRank: !!summary.efficiencyRank ? summary.efficiencyRank.toString() : "---",
            commercialClaimsLabel: !!summary.commercialClaimsLabel ? summary.commercialClaimsLabel : "---",
            medicaidClaimsLabel: !!summary.medicaidClaimsLabel ? summary.medicaidClaimsLabel : "---",
            medicareClaimsLabel: !!summary.medicareClaimsLabel ? summary.medicareClaimsLabel : "---",
            totalClaimsLabel: !!summary.totalClaimsLabel ? summary.totalClaimsLabel : "---",
            networkCount: printNumberAsString(summary.networkCount, "---"),
            networkList: formatNetworkList(summary.networkList),
          };
          /* eslint-enable sort-keys */
        });

        setDashboardDetailsViewToppings(waffleToppings);
      }

      setLoading(false);
    }

    const filtersPayload: NetworkComparisonFiltersPayload = benchmarkingContext.getFormattedFiltersPayload(cloneDeep(props.filtersPayload));

    benchmarkingFiltersContext.getService().getDashboardDetailsData(filtersPayload).then((data) => {
      buildData(data);
    });
  }

  return (
    <React.Fragment>
      <PageHeader disableHR={true} title="Dashboard Details">
        <PageHeaderRight>
          <QESButton
            className="ml-3"
            onClick={() => props.onBackToFullViewClick()}
            qesTestId="benchmarking-dashboard__dashboard-details--back-to-full-view"
            qesType="secondary"
          >
            Back to Dashboard
          </QESButton>

          <NewActionMenu
            alignRight={true}
            buttonClassNames="qes-button-primary ml-3"
            buttonText="Create Report"
            containerClassnames="benchmarking-create-report"
            testId="benchmarking-dashboard--dashboard-details--create-report"
          >
            {Object.values(BenchmarkingDashboardDetailsReportTypeEnum).map((reportType, index) => (
              <FlyoutLink
                classNames="new-action-menu-item"
                key={index}
                size="40"
                testId={`benchmarking-dashboard--dashboard-details__create-report-flyout-link-${index}`}
                content={{
                  body: (
                    <BenchmarkingDashboardDetailsReportFlyout
                      reportType={reportType}
                    />
                  ),
                  title: `Create Report - ${reportType}`,
                }}
              >
                {reportType}
              </FlyoutLink>
            ))}
          </NewActionMenu>
        </PageHeaderRight>
      </PageHeader>
      <BenchmarkingFiltersOverview />
      {(!loading && props.totalCount > 0) && (
        <div className="row">
          <div className="col-12">
            <label className="copy-text-default mb-3">
              You are viewing {printNumberAsString(dashboardDetailsViewToppings.length, "---")} of {printNumberAsString(props.totalCount, "---")} results.
              {props.totalCount > 500 && " To see all results, create a report using the options on the right."}
            </label>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <Waffles<BenchmarkingDashboardDetailsViewWaffleToppings>
            columns={columnConfig}
            customSort={customSort}
            data={dashboardDetailsViewToppings}
            fullWidth={true}
            loading={loading}
            testId="benchmarking-dashboard__dashboard-details--table"
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export default DashboardDetailsView;
